import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AgentWizard } from '@/components/agents/wizard'
import { useProjectStore } from '@/stores/project-store'
import { useAgentWizardStore } from '@/stores/agent-wizard-store'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ArrowLeft, FolderOpen } from 'lucide-react'

export function CreateAgentPage() {
  const { t } = useTranslation(['agents', 'common'])
  const navigate = useNavigate()
  const selectedProject = useProjectStore((state) => state.selectedProject)
  const resetWizard = useAgentWizardStore((state) => state.reset)

  // Reset wizard when entering the page
  useEffect(() => {
    // Only reset if there's no data (fresh navigation)
    // This preserves data if user navigates away and back
  }, [])

  // Handle successful agent creation
  const handleSuccess = (_agentId: string) => {
    // Could navigate to agent details page in the future
    navigate('/agents')
  }

  // Handle cancel
  const handleCancel = () => {
    resetWizard()
    navigate('/agents')
  }

  // If no project selected, show message
  if (!selectedProject) {
    return (
      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center p-6">
        <Card variant="glass" className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-amber-500/20 flex items-center justify-center">
            <FolderOpen className="h-8 w-8 text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            {t('agents:create.noProjectTitle')}
          </h2>
          <p className="text-gray-400 mb-6">
            {t('agents:create.noProjectDesc')}
          </p>
          <Button
            onClick={() => navigate('/agents')}
            variant="outline"
            className="border-gray-700 text-gray-300"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('agents:create.backToAgents')}
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <button
          onClick={() => navigate('/agents')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">{t('agents:create.backToAgents')}</span>
        </button>
        <h1 className="text-2xl font-bold text-white">{t('agents:create.title')}</h1>
        <p className="text-gray-400 mt-1">
          {t('agents:create.subtitle', { count: 6 })}
        </p>
      </div>

      {/* Wizard Component */}
      <AgentWizard onSuccess={handleSuccess} onCancel={handleCancel} />
    </div>
  )
}

export default CreateAgentPage
